import _ from 'lodash';
import moment from 'moment';

export default function createDemoReportingDataset({
  getAgents,
  getTopics,
  getAgentStatusReasons,
  now = () => moment(),
}) {
  let reports;
  let reportsKey;

  return {
    customers: [],

    get summaryLiveboard() {
      return createSummaryLiveboard(now());
    },

    get agents() {
      return getAgents();
    },

    get topics() {
      return getTopics();
    },

    get agentStatusReasons() {
      return getAgentStatusReasons();
    },

    get reports() {
      const agents = getAgents();
      const topics = getTopics();
      const key = JSON.stringify([_.map(agents, 'id'), _.map(topics, topic => [topic.id, topic.parentId || null])]);
      if (!reports || key !== reportsKey) {
        reports = createReports({ agents, topics });
        reportsKey = key;
      }
      return reports;
    },
  };
}

const HOURLY_NEW_CONVERSATIONS = [
  21, 14, 9, 7, 8, 15, 34, 58, 86, 104, 112, 108, 101, 97, 103, 99, 88, 76, 64, 55, 47, 41, 33, 26,
];
const HOURLY_CLOSED_CONVERSATIONS = [
  18, 15, 11, 8, 6, 9, 21, 39, 67, 91, 104, 109, 103, 96, 98, 101, 94, 83, 70, 59, 50, 44, 37, 29,
];

export function createSummaryLiveboard(now) {
  const startOfDay = moment(now).startOf('day');
  const hoursSoFar = moment(now).diff(startOfDay, 'hours') + 1;

  return {
    agent: {
      ready: 83,
      busy: 272,
      away: 51,
      online: 400,
      readyForPhone: 100,
      readyForChat: 53,
      readyForNonInteractive: 3,
      onCall: 100,
      onlineForPhone: 175,
      onlineForChat: 185,
      onlineForNonInteractive: 55,
      freeChatCapacity: 58,
    },
    channel: {
      email: {
        currentNewEmails: 105,
        currentOpenEmails: 116,
        dailyTotalResponses: 122,
        currentWait: 1300000,
        dailyAverageResponseTime: 1400000,
        oldestUnanswered: {},
      },
      phone: {
        currentNewCalls: 153,
        currentActiveCalls: 164,
        dailyTotalCalls: 171,
        dailyOutgoingCalls: 182,
        currentWait: 190000,
        dailyAverageCallTime: 200000,
        oldestUnanswered: {},
      },
      sms: {
        currentNewSms: 36,
        currentOpenSms: 47,
        dailyTotalResponses: 98,
        currentWait: 900000,
        dailyAverageResponseTime: 920000,
        oldestUnanswered: {},
      },
      fbMessage: {
        currentNewMessages: 56,
        currentOpenMessages: 37,
        dailyTotalResponses: 101,
        currentWait: 150000,
        dailyAverageResponseTime: 220000,
        oldestUnanswered: {},
      },
      chat: {
        currentNewChats: 12,
        currentActiveChats: 25,
        dailyTotalChats: 132,
        currentWait: 1200000,
        dailyAverageChatTime: 750000,
        oldestUnanswered: {},
      },
      instagramDirect: {
        currentNewMessages: 28,
        currentOpenMessages: 8,
        currentWait: 480000,
        dailyAverageResponseTime: 820000,
        dailyTotalResponses: 458,
        oldestUnanswered: {},
      },
      twitter: {
        currentNewMessages: 17,
        currentOpenMessages: 10,
        currentWait: 600000,
        dailyAverageResponseTime: 920000,
        dailyTotalResponses: 503,
        oldestUnanswered: {},
      },
      whatsapp: {
        currentNewMessages: 22,
        currentOpenMessages: 6,
        currentWait: 420000,
        dailyAverageResponseTime: 920000,
        dailyTotalResponses: 453,
        oldestUnanswered: {},
      },
    },
    conversation: {
      openAssigned: 233,
      openUnassigned: 44,
      waiting: 471,
    },
    sla: {
      email: { over: 56, total: 982 },
      phone: { over: 22, total: 961 },
      sms: { over: 23, total: 476 },
      fbMessage: { over: 29, total: 354 },
      instagramDirect: { over: 11, total: 87 },
      chat: { over: 7, total: 54 },
      whatsapp: { over: 12, total: 90 },
      twitter: { over: 3, total: 40 },
    },
    trend: _.range(hoursSoFar).map(hour => ({
      timestamp: startOfDay.clone().add(hour, 'hours').toISOString(),
      newConversations: HOURLY_NEW_CONVERSATIONS[hour],
      closedConversations: HOURLY_CLOSED_CONVERSATIONS[hour],
    })),
  };
}

const AGENT_RESPONSE_ROWS = [
  [5, 6, 7, 3, 2, 5, 4],
  [2, 0, 0, 4, 2, 7, 8],
  [15, 6, 0, 12, 4, 6, 9],
  [8, 10, 9, 11, 10, 9, 13],
  [7, 4, 2, 4, 0, 0, 1],
  [3, 5, 2, 7, 5, 4, 6],
  [4, 3, 1, 1, 0, 0, 1],
  [8, 11, 3, 0, 4, 8, 3],
  [4, 9, 3, 5, 6, 0, 3],
  [6, 2, 3, 6, 7, 3, 11],
  [2, 6, 7, 2, 10, 5, 1],
  [9, 4, 3, 2, 6, 6, 1],
  [14, 4, 2, 8, 2, 5, 3],
];

const SPECIFIC_GROUP_AGENT_RESPONSE_ROWS = [
  [3, 5, 2, 3, 0, 1, 3],
  [2, 0, 0, 1, 1, 3, 4],
  [4, 3, 1, 1, 0, 0, 1],
  [6, 3, 5, 2, 5, 2, 1],
  [1, 1, 2, 8, 2, 4, 1],
  [4, 2, 3, 5, 2, 6, 0],
  [1, 4, 2, 6, 4, 2, 1],
  [3, 2, 4, 3, 6, 3, 1],
  [4, 2, 4, 2, 5, 4, 1],
  [2, 3, 5, 2, 3, 1, 2],
  [0, 1, 2, 7, 3, 5, 2],
];

const FILTERED_TOPIC_ROWS = [
  [12, 27, 7, 19, 13, 9, 22, 13, 10, 24],
  [22, 11, 22, 24, 12, 9, 11, 18, 12, 14],
  [10, 19, 21, 9, 15, 20, 13, 18, 14, 20],
  [15, 10, 15, 21, 18, 21, 13, 19, 14, 9],
];

const TOPIC_ROW_LENGTH = 10;

export function createReports({ agents, topics }) {
  return {
    summary: {
      newConversationCounts: [2400, 2400, 2400, 2400, 2400, 2400, 2400],
      closedConversationCounts: [2200, 2200, 2400, 2600, 2800, 3100, 3400],
      totalCloseTime: [100, 330, 220, 200, 200, 300, 230],
      totalResponseTime: [100, 150, 73, 93, 100, 200, 180],
      totalTimeUtilized: [100, 300, 200, 100, 100, 400, 200],
      totalTimeLoggedIn: [170, 600, 350, 180, 190, 750, 300],
      amountOverSLA: [
        [10, 2000],
        [10, 1200],
        [20, 1050],
        [10, 1500],
        [2, 1600],
        [20, 2200],
        [50, 1800],
      ],
      incomingCallCounts: [300, 327, 298, 207, 288, 319, 380],
      outgoingCallCounts: [309, 327, 298, 207, 288, 319, 380],
      abandonedCallCounts: [2, 3, 3, 1, 2, 4, 3],
      totalTalkTime: [200, 220, 180, 120, 130, 230, 200],
      incomingEmailCounts: [25, 35, 24, 27, 30, 29, 32],
      outgoingEmailCounts: [26, 31, 27, 25, 29, 30, 31],
      incomingSmsCounts: [25, 35, 24, 27, 30, 29, 32],
      outgoingSmsCounts: [26, 31, 27, 25, 29, 30, 31],
      declinedAndMissedChats: [1, 2, 1, 2, 4, 5, 1],
      incomingChatMessages: [89, 48, 108, 79, 90, 94, 113],
      outgoingChatMessages: [101, 60, 120, 91, 95, 121, 109],
    },
    agent: {
      allGroups: _.fromPairs(_.map(agents, agent => [agent.id, _.sample(AGENT_RESPONSE_ROWS)])),
      specificGroup: _.fromPairs(_.map(agents, agent => [agent.id, _.sample(SPECIFIC_GROUP_AGENT_RESPONSE_ROWS)])),
    },
    topics: {
      allTopics: createTopicCounts(topics),
      filteredTopics: _.fromPairs(_.map(_.sampleSize(topics, 5), topic => [topic.id, _.sample(FILTERED_TOPIC_ROWS)])),
    },
    'ivr-summary': {
      incomingIvrCallsByTime: [543, 135, 533, 356, 665, 331, 299],
      callsReachingEndStateByTime: [314, 82, 366, 133, 395, 212, 195],
      speechIvrAttemptsByTime: [143, 342, 234, 342, 342, 623, 643],
      unmappedSpeechAttemptsByTime: [22, 28, 32, 42, 29, 33, 19],
      touchtoneIvrAttemptsByTime: [232, 514, 333, 323, 258, 253, 211],
      unmappedTouchtoneAttemptsByTime: [26, 29, 35, 20, 45, 38, 36],
      repeatCallerCountByTime: [11, 13, 9, 22, 19, 24, 15],
    },
    'ivr-end-states': {
      endStates: ['Forward: Customer Care', 'Forward: Additional Support', 'Voicemail', 'inbox 1'],
      endStateDuration: [350000, 42100000, 62000000, 2100000, 520000, 630000],
      endStateCount: [145, 12, 621, 209, 10, 25],
    },
  };
}

function createTopicCounts(topics) {
  const topicIds = new Set(_.map(topics, 'id'));
  const childrenByParentId = _.groupBy(
    _.filter(topics, topic => topic.parentId && topicIds.has(topic.parentId)),
    'parentId'
  );
  const counts = {};

  function countsFor(topic, ancestorIds) {
    if (counts[topic.id]) {
      return counts[topic.id];
    }
    const children = _.reject(childrenByParentId[topic.id], child => ancestorIds.has(child.id));
    if (_.isEmpty(children)) {
      counts[topic.id] = _.times(TOPIC_ROW_LENGTH, () => _.random(0, 500));
    } else {
      const childAncestorIds = new Set([...ancestorIds, topic.id]);
      const childCounts = children.map(child => countsFor(child, childAncestorIds));
      counts[topic.id] = _.times(TOPIC_ROW_LENGTH, i => _.sumBy(childCounts, row => row[i]));
    }
    return counts[topic.id];
  }

  _.forEach(topics, topic => countsFor(topic, new Set()));
  return counts;
}
